import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { ContentListPage } from '../components/ContentListPage';
import { ShareSidebar } from '../components/article/ShareSidebar';
import { ArticleContent } from '../components/article/ArticleBlockRenderer';
import { RecentChangelogsSidebar } from '../components/changelog/RecentChangelogsSidebar';
import { useCachedPublicGet } from '../hooks/useCachedPublicGet';
import { useCachedPublishedArticles } from '../hooks/useCachedPublishedArticles';
import type { ArticleRow } from '../../../shared/schemas/article';

export function ChangelogPage() {
  const { slug } = useParams<{ slug: string }>();
  const entryPath = slug ? `/api/articles/${slug}` : undefined;
  const {
    data: entry,
    loading: entryLoading,
    error: entryError,
  } = useCachedPublicGet<ArticleRow>(entryPath);
  const { data: recent } = useCachedPublishedArticles(
    '/api/articles?type=changelog&limit=10',
  );

  const isReady = Boolean(entry) && !entryLoading;
  const sidebarReady = recent !== null;
  const contentReady = isReady && sidebarReady;
  const showError = !entryLoading && Boolean(entryError || (slug && !entry));
  const pageUrl = `${window.location.origin}/changelog/${slug ?? ''}`;

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(pageUrl).then(
      () => { /* success */ },
      (err: unknown) => {
        console.warn('[ChangelogPage:copyLink] clipboard write failed', { err, pageUrl });
      },
    );
  }, [pageUrl]);

  const leftPanel = sidebarReady ? (
    <RecentChangelogsSidebar entries={recent} currentSlug={slug ?? ''} />
  ) : null;

  if (showError) {
    return (
      <ContentListPage title="Changelog not found" leftPanel={leftPanel}>
        <p className="text-body text-text-tertiary">
          {entryError ?? 'Changelog not found'}
        </p>
        <a href="/changelog" className="text-caption link-default">
          Browse all updates
        </a>
      </ContentListPage>
    );
  }

  return (
    <ContentListPage
      title={entry?.title ?? ''}
      contentReady={contentReady}
      leftPanel={leftPanel}
      rightPanel={<ShareSidebar articleUrl={pageUrl} />}
    >
      {entry && (
        <>
          <ArticleContent content={entry.content} />

          <button
            type="button"
            onClick={handleCopyLink}
            className="site-btn-secondary site-btn-md"
          >
            Copy link
            <ArrowUpRight size={14} />
          </button>
        </>
      )}
    </ContentListPage>
  );
}
